import { useMemo, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { GameDetailBody, GameNotFoundBody, gameSeo } from '@fias/public-site-ui';
import { ApiError } from '@/lib/api';
import { GAME_GENRE_OPTIONS } from '@/lib/game-genres';
import { browserPinsOnlyAtPageLoad } from '@/lib/page-manifest';
import { siteOrigin, usePageHead } from '@/lib/document-head';
import { useGameDetail } from '../hooks/useGamesCatalog';
import { GamesLoadFailed, GamesLoading } from './GamesStatus';

const GENRE_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  GAME_GENRE_OPTIONS.map((g) => [g.id, g.label]),
);

/**
 * One game's page: `/a/games/game/<slug>`. On an iPhone or iPad it says how
 * to put the game on the Home Screen — from the game itself, which is the
 * address the icon will open.
 */
export function GameDetailPage(): ReactElement {
  const { slug = '' } = useParams();
  const detail = useGameDetail(slug);
  const pinsAtLoad = useMemo(() => browserPinsOnlyAtPageLoad(), []);
  const seo = detail.data ? gameSeo(detail.data, siteOrigin() ?? '', GENRE_LABELS) : null;
  usePageHead(
    seo
      ? {
          title: seo.title,
          description: seo.description,
          canonicalPath: seo.canonicalPath,
          jsonLd: siteOrigin() ? seo.jsonLd : [],
        }
      : null,
  );

  if (detail.error instanceof ApiError && detail.error.status === 404) {
    return <GamesNotFoundPage />;
  }
  if (detail.isError && !detail.data) {
    return <GamesLoadFailed what="This game" onRetry={() => void detail.refetch()} />;
  }
  if (!detail.data) return <GamesLoading />;
  return (
    <GameDetailBody
      data={detail.data}
      extra={
        pinsAtLoad ? (
          <p className="fg-detail__note fg-detail__hint">
            To keep it on your Home Screen, open the game, then tap Share and Add to Home Screen.
          </p>
        ) : undefined
      }
    />
  );
}

/** Not a game here — kept out of search results. */
export function GamesNotFoundPage(): ReactElement {
  usePageHead({ title: 'Game not found | Fias', noindex: true });
  return <GameNotFoundBody />;
}
